import ModalLayout from "@/components/layout/ModalLayout";
import { useRef, useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import {
  useDriverOrderImages,
  useUploadDriverOrderImage,
  useDeleteDriverOrderImage,
  getDriverOrderImageUrl,
  DriverOrderImage,
} from "@/hooks/dispatch/driver_order/useDriverOrderImage";

const MAX_PER_FLOOR = 2;

/* 층(송장) 정의: 좌측 리스트에 사용 */
type FloorId = 0 | 1 | 2 | 3;
const FLOORS: { id: FloorId; name: string; imageTypes: string[] }[] = [
  { id: 0, name: "상차송장", imageTypes: ["LOADING"] },
  { id: 1, name: "하차 1 송장", imageTypes: ["UNLOADING1_INVOICE1", "UNLOADING1_INVOICE2"] },
  { id: 2, name: "하차 2 송장", imageTypes: ["UNLOADING2_INVOICE1", "UNLOADING2_INVOICE2"] },
  { id: 3, name: "하차 3 송장", imageTypes: ["UNLOADING3_INVOICE1", "UNLOADING3_INVOICE2"] },
];

export default function PictureOp({
  isOpen,
  onClose,
  orderSlipCode,
}: {
  isOpen: boolean;
  onClose: () => void;
  orderSlipCode?: string | null;
}) {
  const [selectedFloor, setSelectedFloor] = useState<FloorId>(0);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  /* === 프레임(두 장) 편집 모드 상태 === */
  const [slotMode, setSlotMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [slotFiles, setSlotFiles] = useState<(File | null)[]>([null, null]);
  const [slotPreviews, setSlotPreviews] = useState<(string | null)[]>([null, null]);
  const [slotTarget, setSlotTarget] = useState<number | null>(null);
  // 기존 이미지 ID를 추적 (수정 시 삭제용)
  const [existingImageIds, setExistingImageIds] = useState<(number | null)[]>([null, null]);

  /* 라이트박스(원본 보기) 상태 */
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const openPreview = (src: string) => setPreviewSrc(src);
  const closePreview = () => setPreviewSrc(null);

  // API 훅
  const {
    data: imagesData,
    isLoading,
    refetch,
  } = useDriverOrderImages(orderSlipCode || "", {
    enabled: isOpen && !!orderSlipCode,
  });
  const uploadMutation = useUploadDriverOrderImage();
  const deleteMutation = useDeleteDriverOrderImage();

  // 이미지 타입별로 그룹화
  const imagesByFloor = useMemo(() => {
    const result: Record<FloorId, { url: string; imageId: number }[]> = {
      0: [],
      1: [],
      2: [],
      3: [],
    };

    console.log("[PictureOp] orderSlipCode:", orderSlipCode);
    console.log("[PictureOp] imagesData:", imagesData);

    if (!imagesData) return result;

    imagesData.forEach((img: DriverOrderImage) => {
      console.log("[PictureOp] processing image:", img);
      const floor = FLOORS.find(f => f.imageTypes.includes(img.imageType));
      if (floor !== undefined) {
        const url = getDriverOrderImageUrl(img.imageId);
        console.log("[PictureOp] image URL:", url);
        result[floor.id].push({
          url,
          imageId: img.imageId,
        });
      }
    });

    console.log("[PictureOp] imagesByFloor result:", result);
    return result;
  }, [imagesData, orderSlipCode]);

  /* ESC로 라이트박스 닫기 */
  useEffect(() => {
    if (!previewSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewSrc]);

  /* 헬퍼 */
  const getImages = (f: FloorId) => imagesByFloor[f] ?? [];
  const getCount = (f: FloorId) => getImages(f).length;

  /* 모달 열릴 때 초기화 */
  useEffect(() => {
    if (!isOpen) return;
    setSelectedFloor(0);
    setCurrentImageIndex(0);
    clearSlots();
    setSlotMode(false);
  }, [isOpen]);

  /* 좌측 리스트 클릭 (프레임 모드일 때만 전환 제한) */
  const handleFloorClick = (id: FloorId) => {
    if (slotMode && id !== selectedFloor) {
      toast.info("사진 편집 중입니다. 저장 또는 취소 후 다시 시도해주세요.");
      return;
    }
    setSelectedFloor(id);
    setCurrentImageIndex(0);
  };

  /* 기존 보기 모드 좌/우 이동 */
  const handlePrevImage = () => {
    const count = getCount(selectedFloor);
    if (count > 1) setCurrentImageIndex(p => (p > 0 ? p - 1 : count - 1));
  };
  const handleNextImage = () => {
    const count = getCount(selectedFloor);
    if (count > 1) setCurrentImageIndex(p => (p < count - 1 ? p + 1 : 0));
  };

  /* 프레임 모드 유틸 */
  const setSlotFile = (i: number, f: File | null) => {
    setSlotFiles(prev => {
      const next = [...prev];
      next[i] = f;
      return next;
    });
    setSlotPreviews(prev => {
      const next = [...prev];
      if (next[i] && next[i]?.startsWith("blob:")) URL.revokeObjectURL(next[i]!);
      next[i] = f ? URL.createObjectURL(f) : null;
      return next;
    });
    // 새 파일 선택 시 기존 이미지 ID 유지 (저장 시 삭제 처리)
  };
  const removeSlot = (i: number) => {
    setSlotFile(i, null);
    setSlotPreviews(prev => {
      const next = [...prev];
      next[i] = null;
      return next;
    });
  };
  const clearSlots = () => {
    slotPreviews.forEach(u => u && u.startsWith("blob:") && URL.revokeObjectURL(u));
    setSlotFiles([null, null]);
    setSlotPreviews([null, null]);
    setSlotTarget(null);
    setExistingImageIds([null, null]);
  };
  const openSlotPicker = (i: number) => {
    setSlotTarget(i);
    fileInputRef.current?.click();
  };

  /* 프레임 저장: 업로드 + 기존 이미지 삭제 */
  const handleSaveSlots = async () => {
    if (!orderSlipCode) {
      toast.error("주문 코드가 없습니다.");
      return;
    }

    const imageType = FLOORS[selectedFloor].imageTypes[0];

    try {
      // 1. 기존 이미지 중 삭제할 것들 처리
      for (let i = 0; i < 2; i++) {
        const existingId = existingImageIds[i];
        // 기존 이미지가 있었는데 슬롯이 비어있거나 새 파일로 대체된 경우 삭제
        if (existingId && (!slotPreviews[i] || slotFiles[i])) {
          await deleteMutation.mutateAsync({ imageId: existingId, orderSlipCode });
        }
      }

      // 2. 새 파일 업로드 (관리자 업로드 시 driverCode = "SYST")
      for (let i = 0; i < 2; i++) {
        const file = slotFiles[i];
        if (file) {
          await uploadMutation.mutateAsync({
            file,
            orderSlipCode,
            imageType,
            driverCode: "SYST",
          });
        }
      }

      toast.success("사진이 저장되었습니다.");
      refetch();
      clearSlots();
      setSlotMode(false);
    } catch (error) {
      toast.error("사진 저장에 실패했습니다.");
    }
  };

  const cancelSlots = () => {
    clearSlots();
    setSlotMode(false);
  };

  // 편집 모드 진입 시 기존 이미지 프리필
  const enterEditMode = () => {
    if (slotMode) {
      toast.info("사진 편집 중입니다. 저장 또는 취소 후 다시 시도해주세요.");
      return;
    }
    clearSlots();
    const imgs = getImages(selectedFloor);

    const newPreviews: (string | null)[] = [null, null];
    const newExistingIds: (number | null)[] = [null, null];

    imgs.slice(0, 2).forEach((img, i) => {
      newPreviews[i] = img.url;
      newExistingIds[i] = img.imageId;
    });

    setSlotPreviews(newPreviews);
    setExistingImageIds(newExistingIds);
    setSlotMode(true);
  };

  /* 숨김 파일 입력 */
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      className="hidden"
      onChange={e => {
        const f = e.target.files?.[0];
        if (slotTarget !== null && f) setSlotFile(slotTarget, f);
        setSlotTarget(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }}
    />
  );

  const isSaving = uploadMutation.isPending || deleteMutation.isPending;

  return (
    <>
      {fileInput}

      <ModalLayout isOpen={isOpen} onClose={onClose} title="운행사진">
        <div className="flex h-[600px] gap-6">
          {/* 좌측 리스트 */}
          <div className="w-80 flex flex-col gap-3">
            {FLOORS.map(f => (
              <div
                key={f.id}
                onClick={() => handleFloorClick(f.id)}
                className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all
                  ${
                    selectedFloor === f.id
                      ? "bg-[#83C41C] text-[#FFF] shadow-md font-bold"
                      : "bg-[#F8F9FA] text-[#333] hover:bg-[#E9ECEF]"
                  }`}
              >
                <span className="font-medium text-base">{f.name}</span>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold bg-[#D0E7AC] text-[#333]">
                  {getCount(f.id)}
                </div>
              </div>
            ))}
          </div>

          {/* 오른쪽 영역 */}
          <div className="flex-1 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center relative">
            {isLoading ? (
              <div className="text-gray-500">이미지를 불러오는 중...</div>
            ) : (
              <div className="text-center w-full h-full flex items-center justify-center p-6">
                {/* (A) 이미지 없음 + 안내 */}
                {getCount(selectedFloor) === 0 && !slotMode ? (
                  <div className="text-gray-500 text-center">
                    <svg
                      className="w-16 h-16 mx-auto mb-4 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-lg font-medium">이미지가 없습니다</p>
                    <p className="text-sm">해당 송장에 등록된 사진이 없습니다</p>

                    <Button
                      variant="primary"
                      className="mt-4"
                      onClick={() => {
                        setSlotMode(true);
                        clearSlots();
                      }}
                    >
                      사진 등록
                    </Button>
                  </div>
                ) : slotMode ? (
                  /* (B) 프레임 모드 */
                  <div className="w-full max-w-3xl">
                    <div className="grid grid-cols-2 gap-4">
                      {[0, 1].map(i => (
                        <div key={i} className="flex flex-col">
                          {/* 버튼 바: 고정 높이로 확보 */}
                          <div className="flex justify-end gap-2 items-center h-10 mb-4">
                            {slotPreviews[i] && (
                              <>
                                <Button variant="etc" onClick={() => openSlotPicker(i)}>
                                  교체
                                </Button>
                                <Button variant="white" onClick={() => removeSlot(i)}>
                                  삭제
                                </Button>
                              </>
                            )}
                          </div>

                          {/* 이미지 박스: 항상 중앙 정렬 */}
                          <div
                            className="relative aspect-[4/3] rounded-lg border border-gray-300 bg-white
                                          flex items-center justify-center overflow-hidden"
                          >
                            {slotPreviews[i] ? (
                              <img
                                src={slotPreviews[i]!}
                                alt={`slot-${i + 1}`}
                                className="max-w-full max-h-full object-contain cursor-zoom-in"
                                onClick={() => openPreview(slotPreviews[i]!)}
                              />
                            ) : (
                              <button
                                type="button"
                                onClick={() => openSlotPicker(i)}
                                className="group w-full h-full flex flex-col items-center justify-center"
                              >
                                <div
                                  className="w-12 h-12 rounded-full border-2 border-dashed border-gray-300
                                                flex items-center justify-center group-hover:border-[#83C41C] transition-colors"
                                >
                                  <svg
                                    className="w-6 h-6 text-gray-400 group-hover:text-[#83C41C]"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 4v16m8-8H4"
                                    />
                                  </svg>
                                </div>
                                <span className="mt-2 text-sm text-gray-500">송장 {i + 1} 추가</span>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 flex justify-center gap-2">
                      <Button variant="primary" onClick={handleSaveSlots} disabled={isSaving}>
                        {isSaving ? "저장 중..." : "저장"}
                      </Button>
                      <Button variant="white" onClick={cancelSlots} disabled={isSaving}>
                        취소
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* (C) 기존 보기 모드 */
                  <div className="w-full max-w-xl mx-auto">
                    {/* 상단 우측: 사진 추가/수정 버튼 */}
                    <div className="flex justify-end mb-4">
                      <Button variant="etc" onClick={enterEditMode}>
                        {getCount(selectedFloor) >= 2 ? "사진 수정" : "사진 추가"}
                      </Button>
                    </div>

                    {/* 이미지 카드: 중앙 정렬, 고정 높이 프레임 */}
                    <div className="relative bg-white border rounded shadow-sm h-[440px] flex items-center justify-center">
                      {getImages(selectedFloor)[currentImageIndex] ? (
                        <img
                          src={getImages(selectedFloor)[currentImageIndex].url}
                          alt={`floor-${selectedFloor}-img-${currentImageIndex}`}
                          className="max-w-full max-h-full object-contain cursor-zoom-in"
                          onClick={() => openPreview(getImages(selectedFloor)[currentImageIndex].url)}
                        />
                      ) : (
                        <div className="text-center text-gray-500">
                          <div className="w-16 h-16 mx-auto mb-2 bg-gray-300 rounded" />
                          <p>이미지 {getCount(selectedFloor) > 1 ? currentImageIndex + 1 : 1}</p>
                        </div>
                      )}

                      {/* 이전/다음 버튼 (사진 안쪽, 양 옆) */}
                      {getCount(selectedFloor) > 1 && (
                        <>
                          <button
                            onClick={handlePrevImage}
                            className="group absolute left-4 top-1/2 -translate-y-1/2 transform
                                      text-[#AAAAAA] border border-[#CCCCCC] p-[6px] rounded cursor-pointer
                                      bg-white transition-all duration-150
                                      hover:bg-[#F5F7FA] hover:text-[#666666] hover:border-[#999999]
                                      active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#83C41C]/50"
                            aria-label="이전 이미지"
                          >
                            <svg
                              className="w-6 h-6 transition-transform duration-150 group-hover:-translate-x-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>

                          <button
                            onClick={handleNextImage}
                            className="group absolute right-4 top-1/2 -translate-y-1/2 transform
                                      text-[#AAAAAA] border border-[#CCCCCC] p-[6px] rounded cursor-pointer
                                      bg-white transition-all duration-150
                                      hover:bg-[#F5F7FA] hover:text-[#666666] hover:border-[#999999]
                                      active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#83C41C]/50"
                            aria-label="다음 이미지"
                          >
                            <svg
                              className="w-6 h-6 transition-transform duration-150 group-hover:translate-x-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>

                    {/* 인디케이터 영역 */}
                    <div className="h-8 flex items-center justify-center">
                      {getCount(selectedFloor) > 1 && (
                        <div className="flex space-x-2">
                          {Array.from({ length: getCount(selectedFloor) }, (_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`w-3 h-3 rounded-full transition-all ${
                                currentImageIndex === index ? "bg-[#83C41C]" : "bg-gray-400"
                              }`}
                              aria-label={`이미지 ${index + 1}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </ModalLayout>

      {/* ==== 라이트박스(원본 보기) ==== */}
      {previewSrc && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img
              src={previewSrc}
              alt="preview"
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={e => e.stopPropagation()}
            />
            <button
              className="absolute -top-10 right-0 px-3 py-1 rounded bg-white text-gray-700 shadow cursor-pointer"
              onClick={closePreview}
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </>
  );
}
